import {useQueryFilter, useSetIdAziendaTabelle} from "@components/dataTable/customHooks";
import {usePagination} from "@components/dataTable/usePagination";
import {useClientSideProcessing} from "@components/dataTable/useClientSideProcessing";
import {CreateButton, useEditor} from "../../../core/editor";
import DTTitle from "../../../components/dataTable/TableTitle";
import DTTable from "../../../components/dataTable/Table";
import Pagination from "../../../components/dataTable/Pagination";
import React, {useEffect, useMemo} from "react";
import * as ElementoRMLXTabellaRecord from "@commons/mapping/ElementoRMLXTabellaRecord";
import {useFactory} from "@commons/utils/useFactory";
import {TabellaElementiRMLRecord} from "@commons/mapping/CentroDiCostoRecord";
import {
    GetTabellaParams,
    useDeleteElementoMutation,
    useGetElementiRMLXTabellaQuery,
    useInsertElementoMutation,
    useLazyGetElementoQuery,
    useMoveElementoMutation,
    useUpdateElementoMutation
} from "@commons/features/azienda/tabelle/aziendaTabelleElementiRML.api";
import AziendaTabelleElementiRMLEditor from "../../../features/azienda/tabelle/aziendaTabelleElementiRML.editor";
import * as Buttons from "../../../components/dataTable/buttons"
import BasicBadge from "../../../components/BasicBadge";
import LegendaRML from "./LegendaRML";
import {DettaglioRetribuzioneRecord} from "@commons/mapping/DettaglioRetribuzioneRecord";

interface ElementiRMLXTabellaPageProps {
    master: Pick<TabellaElementiRMLRecord, 'idAzienda' | 'idTipo' | 'id'>
}

const ElementiRMLXTabellaPage: React.FC<ElementiRMLXTabellaPageProps> = ({master}) => {
    const pagination = usePagination('',1, 10);
    const {page, setPage, pageLength, setPageLength, search, setSearch} = pagination;

    const [getTrigger, result, promiseInfo] = useLazyGetElementoQuery();
    const [insertTrigger, insertResult] = useInsertElementoMutation();
    const [updateTrigger, updateResult] = useUpdateElementoMutation();
    const [moveTrigger, moveResult] = useMoveElementoMutation();
    const [deleteTrigger, deleteResult] = useDeleteElementoMutation();
    const {
        resource,
        create,
        cancel,
        URI,
        loadResource,
        saveResource,
        deleteResource
    } = useEditor<ElementoRMLXTabellaRecord.Type>(insertTrigger, getTrigger, updateTrigger, deleteTrigger)

    const {filter, setFilter, skip} = useQueryFilter<GetTabellaParams>({} as GetTabellaParams, pagination);
    useSetIdAziendaTabelle({idAziendaCDIC: master.idAzienda}, setFilter, cancel);
    useEffect(() => {
        setFilter(f => ({...f, idAzienda: master.idAzienda, codice: master.id!}));
        cancel();
    }, [master, setFilter, cancel])

    const {data} = useGetElementiRMLXTabellaQuery(filter, {skip: skip || !filter.idAzienda});
    const localData = useClientSideProcessing<ElementoRMLXTabellaRecord.Type, keyof ElementoRMLXTabellaRecord.Type>(data, pagination, ['codElemento', 'descrizione']);

    //const defaultValues = useFactory(CentroDiCostoRecord.factory, {idAzienda: filter.idAzienda!, idTipo: 50});
    // E' preferibile l'uso di useMemo, così da non istanziare ogni volta un nuovo defaultValues
    const params = useMemo(() => {
        return {idAzienda: master.idAzienda, idTipo: master.idTipo, codice: master.id!};
    }, [master]);
    const defaultValues = useFactory(ElementoRMLXTabellaRecord.factory, params); // N.B. useFactory utilizza useMemo!

    function getTipologia(item: ElementoRMLXTabellaRecord.Type) {
        if (typeof item.elementoDiscrezionale === 'undefined') {
            return item.elementoDiscrezionaleDefault ? 'Discrezionale*' : 'CCNL*';
        } else {
            return item.elementoDiscrezionale ? 'Discrezionale' : 'CCNL';
        }
    }

    return (<>
        <div className="px-2 py-3 bg-blue-200 rounded-lg">
            <div className="relative">
                <div className="absolute right-2">
                    <CreateButton<ElementoRMLXTabellaRecord.Type> create={create} label="Elemento"/>
                </div>
            </div>
            {resource && <div className="p-3">
                <AziendaTabelleElementiRMLEditor
                    defaultValues={defaultValues}
                    resource={resource}
                    onSubmit={saveResource}
                    onCancel={cancel}></AziendaTabelleElementiRMLEditor>
            </div>}
            {!resource && <div className="p-5"/>}
        </div>

        <DTTitle id="elencoTabelle" title="Combinazioni per azienda degli elementi RML" {...{currentPageLength: pageLength, setPageLength, currentSearch: search, setSearch}}/>

        <DTTable<ElementoRMLXTabellaRecord.Type> dtSchema={localData}
                                                 header={<>
                                                     <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                         Codice
                                                     </th>
                                                     <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                         Descrizione
                                                     </th>
                                                     <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                         Posizione
                                                     </th>
                                                     <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                         Tipo
                                                     </th>
                                                     <th scope="col" className="py-3.5 w-24"/>
                                                 </>}

                                                 rows={({item, index, isMobile, currentRow, register}) => (
                                                     <tr {...register(item.sortOrder, index, item.URI === URI)}>
                                                         <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                             {item.codElemento}
                                                         </td>

                                                         <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                             {item.descrizione}
                                                         </td>
                                                         <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                             <BasicBadge type={DettaglioRetribuzioneRecord.getType(item.sortOrder)}
                                                                         label={Number(item.sortOrder).toString()}></BasicBadge>
                                                         </td>
                                                         <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                             {getTipologia(item)}
                                                         </td>

                                                         <td className="whitespace-nowrap py-2 text-sm font-medium">
                                                             <Buttons.Visibility visible={isMobile || index === currentRow}>
                                                                 <Buttons.Edit onAction={() => loadResource(item.URI)}/>
                                                                 <Buttons.MoveDown disabled={item.sortOrder >= localData!.recordsTotal}
                                                                                   onAction={() => moveTrigger(item.URI + '?direzione=1')}/>
                                                                 <Buttons.MoveUp disabled={item.sortOrder < 2} onAction={() => moveTrigger(item.URI + '?direzione=-1')}/>
                                                                 <Buttons.Delete onConfirm={() => {
                                                                     deleteResource(item.URI!)
                                                                 }}/>
                                                             </Buttons.Visibility>
                                                         </td>
                                                     </tr>)
                                                 }
        />

        <Pagination dataTables={localData} currentPage={page} setPage={setPage} currentPageLength={pageLength}></Pagination>
        <LegendaRML/>
    </>)
}
export default ElementiRMLXTabellaPage;
